import React from "react";
import { Users, Building2, BookOpen, UserPlus, Edit2, Trash2, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";

const UserAssignmentBadges = ({ user, sites, studies }) => {
  if (user.role === 'qc') {
    if (!user.studies?.length) return <span className="text-gray-400 text-xs">No studies assigned</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {user.studies.map(studyId => {
          const study = studies.find(s => s.id === studyId);
          return study ? <Badge key={studyId} variant="outline" className="text-xs bg-teal-50 text-teal-700 border-teal-200">{study.name}</Badge> : null;
        })}
      </div>
    );
  }
  if (user.role === 'site_coordinator') {
    if (!user.sites?.length) return <span className="text-gray-400 text-xs">No sites assigned</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {user.sites.map(siteId => {
          const site = sites.find(s => s.id === siteId);
          return site ? <Badge key={siteId} variant="outline" className="text-xs">{site.name}</Badge> : null;
        })}
      </div>
    );
  }
  return <span className="text-gray-500">All Studies</span>;
};

export const UsersTab = ({ users, sites, studies, setIsAddUserOpen, openUserEditor, deleteUser }) => (
  <div className="space-y-6" data-testid="admin-users-tab">
    <div className="flex justify-between items-center">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
        <p className="text-gray-600">Manage site coordinators and study leads</p>
      </div>
      <Button onClick={() => setIsAddUserOpen(true)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700" data-testid="add-user-btn">
        <UserPlus className="h-4 w-4" /> Add New User
      </Button>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-blue-900">Total Users</CardTitle>
          <Users className="h-4 w-4 text-blue-600" />
        </CardHeader>
        <CardContent><div className="text-3xl font-bold text-blue-900">{users.length}</div></CardContent>
      </Card>
      <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-green-100">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-green-900">Site Coordinators</CardTitle>
          <Building2 className="h-4 w-4 text-green-600" />
        </CardHeader>
        <CardContent><div className="text-3xl font-bold text-green-900">{users.filter(u => u.role === 'site_coordinator').length}</div></CardContent>
      </Card>
      <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-purple-100">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-purple-900">Study Leads</CardTitle>
          <BookOpen className="h-4 w-4 text-purple-600" />
        </CardHeader>
        <CardContent><div className="text-3xl font-bold text-purple-900">{users.filter(u => u.role === 'study_lead').length}</div></CardContent>
      </Card>
      <Card className="border-0 shadow-lg bg-gradient-to-br from-orange-50 to-orange-100">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-orange-900">QC Team</CardTitle>
          <CheckCircle2 className="h-4 w-4 text-orange-600" />
        </CardHeader>
        <CardContent><div className="text-3xl font-bold text-orange-900">{users.filter(u => u.role === 'qc').length}</div></CardContent>
      </Card>
    </div>

    <Card className="border-0 shadow-lg">
      <CardHeader className="bg-gray-50 rounded-t-lg"><CardTitle className="text-gray-900">All Users</CardTitle></CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left p-4 font-semibold text-gray-700">Name</th>
                <th className="text-left p-4 font-semibold text-gray-700">Email</th>
                <th className="text-left p-4 font-semibold text-gray-700">Role</th>
                <th className="text-left p-4 font-semibold text-gray-700">Assignment</th>
                <th className="text-left p-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user.id} className={`border-b hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`} data-testid={`user-row-${user.id}`}>
                  <td className="p-4 font-medium text-gray-900">{user.name}</td>
                  <td className="p-4 text-gray-600">{user.email}</td>
                  <td className="p-4">
                    <Badge variant={user.role === 'study_lead' ? 'default' : 'secondary'} className="bg-blue-100 text-blue-800">{user.role.replace('_', ' ')}</Badge>
                  </td>
                  <td className="p-4"><UserAssignmentBadges user={user} sites={sites} studies={studies} /></td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openUserEditor(user)} className="hover:bg-blue-50" data-testid={`edit-user-${user.id}`}>
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => deleteUser(user.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-user-${user.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  </div>
);
